import type { WebsitePreviewMetadata } from '@/components/github/GitHubLivePreview';

export const WORKSPACE_WEBSITE_PREVIEW_LAUNCH_EVENT = 'attune:workspace-website-preview-launch';
const pendingLaunches = new Map<string, Promise<WorkspaceWebsitePreviewLaunchResult | null>>();

export interface WorkspaceWebsitePreviewLaunchResult {
  preview: WebsitePreviewMetadata;
  sourceSessionId: string;
}

export interface WorkspaceWebsitePreviewLaunchDetail {
  projectId: string;
  actorUserId: string;
  onAccepted?: () => void;
  onResult?: (result: WorkspaceWebsitePreviewLaunchResult | null) => void;
}

export function requestWorkspaceWebsitePreview(
  projectId: string,
  actorUserId: string,
  options: { waitForListenerMs?: number; isActive?: () => boolean } = {}
): Promise<WorkspaceWebsitePreviewLaunchResult | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const key = JSON.stringify([actorUserId, projectId]);
  const pending = pendingLaunches.get(key);
  if (pending) return pending;
  const request = new Promise<WorkspaceWebsitePreviewLaunchResult | null>(resolve => {
    let settled = false;
    let accepted = false;
    const finish = (result: WorkspaceWebsitePreviewLaunchResult | null) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const deadline = Date.now() + (options.waitForListenerMs || 0);
    const dispatch = () => {
      if (options.isActive && !options.isActive()) {
        finish(null);
        return;
      }
      // Event delivery is synchronous. Once the workspace accepts the launch,
      // wait for its actual result rather than abandoning an active request.
      window.dispatchEvent(
        new CustomEvent<WorkspaceWebsitePreviewLaunchDetail>(
          WORKSPACE_WEBSITE_PREVIEW_LAUNCH_EVENT,
          {
            detail: {
              projectId,
              actorUserId,
              onAccepted: () => {
                accepted = true;
              },
              onResult: finish,
            },
          }
        )
      );
      if (!accepted) {
        if (Date.now() < deadline) setTimeout(dispatch, 100);
        else finish(null);
      }
    };
    dispatch();
  });
  pendingLaunches.set(key, request);
  const clear = () => {
    if (pendingLaunches.get(key) === request) pendingLaunches.delete(key);
  };
  void request.then(clear, clear);
  return request;
}
